import { CloseOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import type { PopconfirmProps } from 'antd';
import { Button, Modal, Space } from 'antd';
import { createStyles } from 'antd-style';
import React, { ReactNode, useState } from 'react';

/**
 * A Modal-backed replacement for Popconfirm that accepts PopconfirmProps
 * and presents as a centered modal with overlay.
 *
 * Notes:
 * - Applies `icon`, `okText`, `cancelText`, `okType`, `okButtonProps`, `cancelButtonProps`, `showCancel`.
 * - Uses `open` (A.K.A. `visible`) to control visibility. For compatibility, we accept either.
 * - Avoids passing Tooltip-only props to Modal.
 */
export type ModalPopconfirmProps = Omit<PopconfirmProps, 'title'> & {
  /** Optional Modal–specific props you may want to expose */
  children: ReactNode;
  title?: string;
  width?: number;
  maskClosable?: boolean;
  closable?: boolean;
  className?: string;
  style?: React.CSSProperties;
  /**
   * When true, the OK button shows a loading spinner.
   * AntD Popconfirm doesn’t have this top-level prop, but it’s commonly used.
   */
  confirmLoading?: boolean;
  /** Color customization props */
  modalBgColor?: string;
  maskBgColor?: string;
  okButtonBgColor?: string;
  okButtonTextColor?: string;
  closeIconColor?: string;
};

const usePopupConfirmStyles = createStyles(
  (
    { token, cx },
    otherProps: {
      modalBgColor?: string;
      maskBgColor?: string;
      okButtonBgColor?: string;
      okButtonTextColor?: string;
      closeIconColor?: string;
    }
  ) => {
    return {
      modal: {
        '.ant-modal-content .ant-modal-close': {
          margin: '20px'
        },
        '.ant-modal-content': {
          backgroundColor: otherProps.modalBgColor,
          borderRadius: '8px'
        },
        '.ant-modal-body': {
          fontSize: '20px',
          padding: '58px 35px 38px 35px',
          textAlign: 'center',
          fontWeight: 400
        },
        // Mask styling via global selector
        ':global(.ant-modal-root .ant-modal-mask)': {
          backgroundColor: otherProps.maskBgColor
        }
      },
      trigger: {
        cursor: 'pointer'
      },
      closeIcon: {
        color: otherProps.closeIconColor,
        fontSize: '20px'
      },
      description: {
        marginBottom: 16
      },
      okButton: {
        backgroundColor: otherProps.okButtonBgColor,
        color: otherProps.okButtonTextColor,
        fontWeight: 600,
        padding: '16px',
        '&:hover': {
          filter: 'brightness(1.05)'
        },
        '&:active': {
          filter: 'brightness(0.95)'
        }
      }
    };
  }
);

export const ModalPopconfirm: React.FC<ModalPopconfirmProps> = ({
  children,
  title,
  description,
  okText = 'OK',
  cancelText = 'Cancel',
  okType = 'primary',
  okButtonProps,
  cancelButtonProps,
  icon = <ExclamationCircleOutlined />,
  showCancel = true,
  onConfirm,
  onCancel,
  confirmLoading = false,
  width = 420,
  maskClosable = false,
  closable = true,
  className,
  style,
  open,
  trigger,
  modalBgColor,
  maskBgColor,
  okButtonBgColor,
  okButtonTextColor,
  closeIconColor
}) => {
  // Controlled vs uncontrolled logic
  const isControlled = typeof open === 'boolean';
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = isControlled ? open : internalOpen;

  const { styles } = usePopupConfirmStyles({
    modalBgColor,
    maskBgColor,
    okButtonBgColor,
    okButtonTextColor,
    closeIconColor
  });

  const handleCancel = () => {
    if (!isControlled) setInternalOpen(false);
    onCancel?.();
  };
  const handleConfirm = () => {
    onConfirm?.();
    if (!confirmLoading && !isControlled) setInternalOpen(false);
  };
  const handleTriggerClick = () => {
    if (!isControlled) setInternalOpen(true);
  };

  return (
    <>
      <span onClick={handleTriggerClick} className={styles.trigger}>
        {children}
      </span>
      <Modal
        open={isOpen}
        title={
          title ? (
            <Space>
              {icon}
              <span>{title}</span>
            </Space>
          ) : null
        }
        footer={null}
        width={width}
        closable={closable}
        maskClosable={maskClosable}
        centered
        closeIcon={<CloseOutlined className={styles.closeIcon} />}
        className={`${styles.modal} ${className ?? ''}`}
        onCancel={handleCancel}
        destroyOnHidden
      >
        {description && (
          <div className={styles.description}>
            {typeof description === 'function' ? description() : description}
          </div>
        )}
        <Space>
          {showCancel && (
            <Button {...cancelButtonProps} onClick={handleCancel}>
              {cancelText}
            </Button>
          )}
          <Button
            className={styles.okButton}
            type={okType as any}
            loading={confirmLoading}
            onClick={handleConfirm}
            {...okButtonProps}
          >
            {okText}
          </Button>
        </Space>
      </Modal>
    </>
  );
};

export default ModalPopconfirm;
